<template>
  <q-card
    v-observe-visibility="{callback: visibilityChanged, once: true}"
    class="q-mb-lg q-mb-sm-md q-mb-lg-lg relative-position widget-card"
    :class="cardClasses"
    :style="cardStyles"
  >
    <q-inner-loading
      :showing="!invisibleCard && loading"
      style="z-index: 1;"
    >
      <loading-spinner />
    </q-inner-loading>

    <div
      v-if="editMode"
      class="absolute-top-right"
      style="top:-8px; right: -12px;z-index: 1;"
    >
      <q-btn-group rounded>
        <q-btn
          v-if="canOpenSettings"
          rounded
          color="white"
          text-color="black"
          icon="mdi-cog-outline"
          @click="isEditingCard = !isEditingCard"
        />
        <q-btn
          rounded
          color="red"
          icon="mdi-close"
          @click="$emit('remove')"
        />
      </q-btn-group>
    </div>

    <template v-if="collapsed">
      <div
        class="row items-center q-px-md q-py-sm widget-collapsed-header cursor-pointer"
        @click="toggleCollapsed"
      >
        <q-icon
          name="mdi-chevron-down"
          size="20px"
          class="q-mr-sm text-grey-6"
        />
        <span class="text-subtitle1 text-weight-medium col">{{ title }}</span>
        <span class="text-caption text-grey-6">Click to expand</span>
      </div>
      <div v-if="$slots.preview" class="widget-collapsed-preview">
        <slot name="preview" />
      </div>
    </template>

    <div
      v-show="!collapsed"
      :class="{'no-pointer-events': editMode}"
      class="relative-position widget-body"
    >
      <slot name="body" />
    </div>

    <q-btn
      v-if="!noCollapse"
      unelevated
      dense
      size="sm"
      color="white"
      text-color="grey-7"
      :icon="collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up'"
      class="widget-collapse-floating"
      @click="toggleCollapsed"
    >
      <q-tooltip>{{ collapsed ? 'Expand' : 'Collapse' }}</q-tooltip>
    </q-btn>

    <!-- Settings dialog -->
    <content-dialog
      v-model="isEditingCard"
      fullscreen-mobile
      title="Widget Settings"
      transition-show="scale"
    >
      <slot name="settings" />
    </content-dialog>
  </q-card>
</template>

<script>
import { ObserveVisibility } from 'vue-observe-visibility'

import widgetMixin from './mixin'

const STORAGE_PREFIX = 'dashboard.widget.collapsed.'

export default {
  directives: {
    ObserveVisibility
  },
  mixins: [widgetMixin],
  props: {
    styles: {
      type: Object,
      default: () => {
        return {}
      }
    },
    canEdit: {
      required: false,
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    minHeight: {
      type: Number,
      default: undefined
    },
    invisibleCard: {
      type: Boolean,
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    noCollapse: {
      type: Boolean,
      default: false
    }
  },
  emits: ['remove', 'load'],
  data () {
    return {
      isEditingCard: false,
      visible: false,
      collapsed: this.noCollapse ? false : this.readCollapsed()
    }
  },
  computed: {
    canOpenSettings () {
      return !!this.$slots.settings
    },
    storageKey () {
      return this.id != null ? `${STORAGE_PREFIX}${this.id}` : null
    },
    cardClasses () {
      return {
        'editing-card': this.isEditingCard,
        'bg-transparent shadow-0': this.invisibleCard,
        'bg-black text-white': this.dark
      }
    },
    cardStyles () {
      if (this.collapsed) {
        return this.styles
      }
      if (this.loading) {
        return {
          'min-height': this.minHeight ? `${this.minHeight}px` : '150px',
          ...this.styles
        }
      }
      return { ...this.styles }
    }
  },
  watch: {
    editMode (val) {
      if (!val) {
        this.isEditingCard = false
      }
    }
  },
  methods: {
    visibilityChanged (visibility) {
      if (!this.visible && visibility) {
        this.visible = true
        this.$emit('load')
      }
    },
    readCollapsed () {
      if (this.id == null) return false
      try {
        return localStorage.getItem(`${STORAGE_PREFIX}${this.id}`) === '1'
      }
      catch (e) {
        return false
      }
    },
    toggleCollapsed () {
      this.collapsed = !this.collapsed
      if (!this.storageKey) return
      try {
        if (this.collapsed) {
          localStorage.setItem(this.storageKey, '1')
        }
        else {
          localStorage.removeItem(this.storageKey)
        }
      }
      catch (e) {
        // localStorage unavailable (private mode, quota) — non-fatal
      }
    }
  }
}
</script>

<style scoped lang="scss">
.widget-card .widget-collapsed-header {
  transition: background-color 0.15s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}

.widget-card .widget-body {
  padding-bottom: 14px;
}

.widget-card .widget-collapse-floating {
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  min-width: 64px;
  min-height: 20px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
</style>
